<script setup lang="ts">
import { computed, onBeforeUnmount, reactive, ref, watch } from "vue";
import { formatNumber, formatNumberForScale } from "chartjs-graph";
import { PlanEditTarget, useBasePriceStore } from "@/modules/base_price/store";
import PlanSeasonalityCalendar from "@/modules/base_price/components/PlanSeasonalityCalendar.vue";
import PlanMonthBreadcrumb from "@/modules/base_price/components/PlanMonthBreadcrumb.vue";
import PlanMonthLabel from "@/modules/base_price/components/PlanMonthLabel.vue";
import PlanPreviousYearsDialog from "@/modules/base_price/components/PlanPreviousYearsDialog.vue";
import {
	daysFitMonth,
	monthDates,
} from "@/modules/base_price/utils/dayDistribution";
import { SEASON_STATE_COLORS, SEASON_STATE_TINTS } from "@/modules/base_price/utils/metricColors";

// Short names for the style block's `v-bind()`.
const AUTO_COLOR = SEASON_STATE_COLORS.auto;
const LOCKED_COLOR = SEASON_STATE_COLORS.locked;
const PINNED_COLOR = SEASON_STATE_COLORS.pinned;
const LOCKED_TINT = SEASON_STATE_TINTS.locked;
const PINNED_TINT = SEASON_STATE_TINTS.pinned;

/**
 * Edits whatever the panel is pointed at — a hierarchy node, or one screen
 * attached to one. The resolver prices both the same way and both carry the
 * same monthly arrays (see ResolvedAssetDTO), so nothing below needs to know
 * which it is; the store decides which column the saved override lands in.
 */
const props = defineProps<{ target: PlanEditTarget }>();

const store = useBasePriceStore();

const MONTHS = [
	"Jan",
	"Feb",
	"Mar",
	"Apr",
	"May",
	"Jun",
	"Jul",
	"Aug",
	"Sep",
	"Oct",
	"Nov",
	"Dec",
];

/** Renormalizes any 12-value array to sum to exactly 100 — matches the original design's pctOf(). */
const pctOf = (values: number[]): number[] => {
	const total = values.reduce((sum, v) => sum + v, 0) || 1;
	return values.map((v) => (v / total) * 100);
};

/**
 * The node's own monthly gross-impression mix, normalized to 100 — the
 * natural rebalance weighting (unlocked months move in proportion to how
 * much inventory they actually have) and the "Follow impression share"
 * assist's target. Also drawn as the dashed reference line.
 */
const impShare = computed<number[]>(() =>
	pctOf(props.target.monthly_gross_impressions)
);

/**
 * A true draft, separate from the store's preview overrides — unlike the
 * Budget split tab, which commits every keystroke, this only reaches the
 * resolver on Save (matching the original design exactly: Budget split has
 * no draft/commit distinction, Seasonality does).
 */
const draft = reactive<number[]>(Array(12).fill(0));
const original = reactive<number[]>(Array(12).fill(0));
const locked = reactive<boolean[]>(Array(12).fill(false));
/** The lock arrangement this draft was seeded with — what Cancel/Undo restore, alongside `original`. */
const originalLocked = reactive<boolean[]>(Array(12).fill(false));

/**
 * Exact euro amounts for individual months; null means the month is not
 * pinned and takes its weight's share of whatever the pins leave. Stored as
 * euros and converted against the current budget on every resolve — the
 * whole point of a pin is that it still reads 40,000 after a split higher up
 * moves this target's budget.
 */
const pins = reactive<(number | null)[]>(Array(12).fill(null));
const originalPins = reactive<(number | null)[]>(Array(12).fill(null));

// Opens in euros: what each month gets is the question this tab answers, and
// a share is the derived reading of it. Shared with the calendar.
const mode = ref<"pct" | "abs">("abs");
const dirty = ref(false);

/**
 * The month open in the calendar (1-12), or null for the twelve-month graph.
 * Kept when the selection moves, so stepping through the hierarchy compares
 * the same December everywhere rather than dropping back to the year.
 */
const calendarMonth = ref<number | null>(null);

const previousYearsOpen = ref(false);

/**
 * The calendar's own reset, surfaced here so it sits in this toolbar beside
 * the toggle rather than in the calendar's body — the month graph's reset
 * lives in the same row.
 */
const calendarRef = ref<InstanceType<typeof PlanSeasonalityCalendar> | null>(
	null
);

const MONTH_NAMES = [
	"January",
	"February",
	"March",
	"April",
	"May",
	"June",
	"July",
	"August",
	"September",
	"October",
	"November",
	"December",
];

/**
 * Months (0-11) this target decides by day itself — months its own
 * locked-days row lists, or with pins of its own — marked like an overridden
 * node in the hierarchy: own settings only, never inherited ones.
 */
const monthsWithDays = computed<Set<number>>(() => {
	const months = new Set<number>();
	const year = String(store.plan?.year ?? "");

	const mark = (days: Record<string, number> | undefined) =>
		Object.keys(days ?? {}).forEach((date) => {
			if (date.startsWith(`${year}-`)) months.add(Number(date.slice(5, 7)) - 1);
		});

	store.targetOverride("day_season")?.months?.forEach((key) => {
		if (key.startsWith(`${year}-`)) months.add(Number(key.slice(5, 7)) - 1);
	});
	mark(store.targetOverride("day_budget")?.daily_amounts);

	return months;
});

/**
 * Months (0-11) whose pinned days don't fit what the month now gets — the
 * same check the resolver makes (see DayPins) and the calendar's own footer
 * shows, so a month lowered below its pinned days is marked where it was
 * lowered rather than only in the Issues dialog. Measured against the applied
 * monthly budget, like the resolver.
 */
const monthsWithDayIssues = computed<Set<number>>(() => {
	const months = new Set<number>();
	const year = store.plan?.year;
	if (!year) return months;

	const pins = store.targetOverride("day_budget")?.daily_amounts ?? {};

	for (let month = 1; month <= 12; month++) {
		if (
			!daysFitMonth(
				monthDates(year, month),
				pins,
				props.target.monthly_budget[month - 1] ?? 0
			)
		) {
			months.add(month - 1);
		}
	}

	return months;
});

/**
 * Opens a month by day. Not while the months themselves have unapplied
 * changes: the calendar divides what a month was *given*, and that is the
 * applied figure, so it would quietly work against a number the graph has
 * already moved away from.
 */
const openMonth = (i: number) => {
	if (dirty.value) return;

	calendarMonth.value = i + 1;
};

const seedFrom = (
	values: number[],
	locks?: boolean[] | null,
	amounts?: (number | null)[] | null
) => {
	const normalized = pctOf(values);
	normalized.forEach((v, i) => {
		draft[i] = Math.round(v * 10) / 10;
		original[i] = draft[i];
		locked[i] = locks?.[i] ?? false;
		originalLocked[i] = locked[i];
		pins[i] = amounts?.[i] ?? null;
		originalPins[i] = pins[i];
	});
	dirty.value = false;
};

/**
 * Restores the configuration that was actually applied, not the resolver's
 * rendering of it.
 *
 * Seeding from the resolved payload would rewrite the configuration on every
 * visit: monthly_season_shares is the *effective* shape with pins already
 * folded in, so a month pinned at €40,000 would come back as the 10% it
 * happened to resolve to and stop being a pin at all. The draft rows are the
 * exact thing that was saved, so they round-trip untouched.
 *
 * Only a target with no row of its own falls back to the resolved shape —
 * that is inheritance, and inheriting the effective shape is precisely
 * right, since that is what this target is actually being handed.
 */
const seedFromDraft = () => {
	const season = store.targetOverride("season");
	const monthBudget = store.targetOverride("month_budget");

	seedFrom(
		season?.monthly_shares ?? props.target.monthly_season_shares,
		season?.locked_months ?? null,
		monthBudget ? Object.values(monthBudget.monthly_amounts ?? {}) : null
	);
};

// Only re-seeds when the *target or book* actually changes — not on every
// resolve (e.g. an unrelated edit elsewhere), which would otherwise wipe an
// in-progress, unsaved seasonality edit out from under the user.
watch(
	// Keyed on kind too: a node id and an asset id are separate spaces and
	// can collide, and switching between them must re-seed the draft.
	() => [props.target.kind, props.target.id, store.selectedBookUuid],
	seedFromDraft,
	{ immediate: true }
);

const isPinned = (i: number) => pins[i] !== null;
const pinnedCount = computed(() => pins.filter((v) => v !== null).length);

const budget = computed(() => props.target.budget);

/** Euros a month actually gets: its pin, or its weight's share of the remainder. */
const monthAmount = (i: number): number => {
	if (pins[i] !== null) return pins[i] as number;

	const freeWeight = draft.reduce(
		(sum, w, j) => sum + (isPinned(j) ? 0 : w),
		0
	);

	return freeWeight > 0 ? (remainingForFree.value * draft[i]) / freeWeight : 0;
};

const pinnedTotal = computed(() =>
	pins.reduce((sum: number, v) => sum + (v ?? 0), 0)
);
const remainingForFree = computed(() =>
	Math.max(0, budget.value - pinnedTotal.value)
);
const freeMonthCount = computed(
	() => pins.filter((v, i) => v === null && draft[i] > 0).length
);

/**
 * The remainder split the way the bar below reports it: locked months hold a
 * share they were given by hand, auto months take what is left over. Both are
 * still weights sharing the same pot — unlike a pin, a lock takes nothing off
 * the top — so this is a breakdown of `remainingForFree`, not a fourth claim
 * on the budget.
 */
const freeWeights = computed(() => {
	let lockedWeight = 0;
	let autoWeight = 0;

	draft.forEach((weight, i) => {
		if (isPinned(i)) return;
		if (locked[i]) {
			lockedWeight += weight;
		} else {
			autoWeight += weight;
		}
	});

	const total = lockedWeight + autoWeight;

	return {
		locked: total > 0 ? (remainingForFree.value * lockedWeight) / total : 0,
		auto: total > 0 ? (remainingForFree.value * autoWeight) / total : 0,
	};
});

/**
 * What the twelve months between them ask for — deliberately uncapped, so an
 * over-committed target reads "Allocated €5.8M of €5.2M" rather than claiming
 * a perfect fit while the line underneath says it is €635K over.
 *
 * The remainder only counts as allocated when something is actually free to
 * take it: with every month pinned there is nothing to absorb the difference,
 * so the total is the pins alone and the shortfall stays visible.
 */
const allocated = computed(
	() =>
		pinnedTotal.value + (freeMonthCount.value > 0 ? remainingForFree.value : 0)
);

/**
 * The same rule Budget split applies across siblings, one dimension over:
 * pins must fit inside the budget, and when nothing is left free to absorb a
 * difference they must land on it exactly. Note what is *not* here any more —
 * the old "the twelve must total exactly 100" gate. Unpinned months are
 * weights that share whatever is left, so being under is not an error; only
 * over-committing is.
 */
const overshoot = computed(() => Math.max(0, pinnedTotal.value - budget.value));
const valid = computed(() => {
	if (overshoot.value > 1) return false;

	return (
		freeMonthCount.value > 0 || Math.abs(pinnedTotal.value - budget.value) <= 1
	);
});

/**
 * Spreads whatever weight is left after the locked months across the
 * unlocked ones, in proportion to their impression share. Pinned months sit
 * outside this entirely — their euros are settled at resolve time, so their
 * weight is not competing for a share of the hundred here.
 */
const rebalance = () => {
	const lockedWeight = locked.reduce(
		(sum, isLocked, i) => sum + (isLocked && !isPinned(i) ? draft[i] : 0),
		0
	);
	const movableIdx = draft
		.map((_, i) => i)
		.filter((i) => !locked[i] && !isPinned(i));

	if (movableIdx.length === 0) return;

	const impShareSum =
		movableIdx.reduce((sum, i) => sum + impShare.value[i], 0) || 1;
	const rest = Math.max(0, 100 - lockedWeight);

	movableIdx.forEach((i) => {
		draft[i] = rest * (impShare.value[i] / impShareSum);
	});
};

/** Editing a month's weight always locks it — it no longer participates in auto-rebalancing. */
const setPct = (i: number, value: number) => {
	draft[i] = Math.min(100, Math.max(0, value));
	locked[i] = true;
	rebalance();
	dirty.value = true;
};

/**
 * Pins a month to an exact euro amount, or clears the pin when given null.
 * A pinned month keeps its weight untouched underneath: clearing the pin
 * drops it straight back into the pool with the share it always had, rather
 * than needing the weight to be reconstructed from the euros.
 */
const setPin = (i: number, amount: number | null) => {
	pins[i] = amount === null ? null : Math.max(0, amount);
	dirty.value = true;
};

const clearAllPins = () => {
	pins.forEach((_, i) => (pins[i] = null));
	dirty.value = true;
};

/**
 * Cycles one month through its three states: AUTO → LOCKED → PINNED → AUTO.
 *
 * A month's weight survives being pinned — setPin() never touches it — so
 * coming back round to AUTO drops it into the pool with the share it always
 * had, rather than one reverse-engineered from the euros it happened to hold.
 */
const cycleState = (i: number) => {
	if (isPinned(i)) {
		setPin(i, null);
		locked[i] = false;
		rebalance();

		return;
	}

	if (locked[i]) {
		// Pins at whatever the month is worth right now, so the step is
		// visually a no-op — what changes is that the amount stops moving.
		setPin(i, Math.round(monthAmount(i)));
		locked[i] = false;

		return;
	}

	locked[i] = true;
	rebalance();
	dirty.value = true;
};

/**
 * Shapes the months after a previous year's shares (see PlanPreviousYearsDialog).
 * Pinned months keep their pin; the others share the hundred in the years'
 * proportions and are locked, so a later edit rebalancing by impressions
 * does not wash the shape out.
 */
const applyPreviousYearsShares = (shares: number[]) => {
	const freeIdx = draft.map((_, i) => i).filter((i) => !isPinned(i));
	const weights = pctOf(freeIdx.map((i) => shares[i] ?? 0));

	freeIdx.forEach((i, n) => {
		draft[i] = Math.round(weights[n] * 10) / 10;
		locked[i] = true;
	});
	dirty.value = true;
};

const followImpressionShare = () => {
	impShare.value.forEach((v, i) => {
		draft[i] = Math.round(v * 10) / 10;
		locked[i] = false;
	});
	dirty.value = true;
};

/**
 * A pinned month shows its exact amount; every other month shows what its
 * weight currently earns it, in whichever unit the display toggle is on.
 * Note there is no longer a euro→percentage conversion on the way *in*: a
 * euro figure entered on a pinned month is stored as euros. That conversion,
 * done at edit time against the budget as it happened to be, is exactly what
 * used to make a "fixed" month quietly stop being fixed as soon as a split
 * higher up moved.
 */
const displayValue = (i: number): number => {
	if (isPinned(i)) return Math.round(pins[i] as number);

	const value = mode.value === "abs" ? monthAmount(i) : draft[i];
	return Math.round(value * 10) / 10;
};

const onInputChange = (i: number, rawValue: number) => {
	if (isPinned(i)) {
		setPin(i, rawValue);
		return;
	}

	setPct(
		i,
		mode.value === "abs" && budget.value > 0
			? (rawValue / budget.value) * 100
			: rawValue
	);
};

/** Whether this month's box is holding euros rather than a percentage. */
const isCurrency = (i: number) => isPinned(i) || mode.value === "abs";

/**
 * Which box is being typed in, if any. A euro figure reads better grouped
 * (1.237.937) but is far easier to edit as plain digits, so it shows grouped
 * everywhere except under the cursor.
 */
const focusedInput = ref<number | null>(null);

/**
 * What the box shows. Euro months are grouped by formatNumber() rather than
 * left as a run of digits — which is also why they are type="text": a native
 * number input cannot display a separator at all.
 */
const displayText = (i: number): string => {
	const value = displayValue(i);

	if (!isCurrency(i)) return String(value);

	return focusedInput.value === i
		? String(value)
		: formatNumber(value, "number_no_fraction");
};

const onInputText = (i: number, raw: string) => {
	onInputChange(
		i,
		isCurrency(i) ? Number(raw.replace(/[^0-9]/g, "") || 0) : Number(raw)
	);
};

const wasChanged = (i: number) => Math.abs(draft[i] - original[i]) > 0.05;

const formatBarValue = (i: number): string =>
	mode.value === "abs" || isPinned(i)
		? formatNumberForScale("money", monthAmount(i))
		: draft[i].toFixed(1);

// --- Chart geometry ---
const chartHeight = 160;
/**
 * Bars are drawn from what each month actually gets, as a share of the
 * target's budget — the one scale a pinned month and a weighted month can
 * both be read on. With nothing pinned this is identical to plotting the
 * weights, so the familiar chart is unchanged.
 */
const monthSharePct = (i: number): number =>
	budget.value > 0 ? (monthAmount(i) / budget.value) * 100 : 0;

const maxPct = computed(
	() =>
		Math.max(
			...Array.from({ length: 12 }, (_, i) => monthSharePct(i)),
			...impShare.value,
			1
		) * 1.2
);
const barHeight = (i: number) =>
	(monthSharePct(i) / maxPct.value) * chartHeight;
const refTop = (i: number) =>
	chartHeight - (impShare.value[i] / maxPct.value) * chartHeight;

// --- Drag-to-edit ---
let dragIndex: number | null = null;
let dragStartY = 0;
let dragStartValue = 0;
let dragPinned = false;

/**
 * Bars are drawn on one scale — share of this target's budget — but the two
 * kinds of month are *stored* differently, so a drag has to land in the right
 * unit. A pinned month's drag moves its euro amount; an unpinned month's
 * moves its weight.
 *
 * The weight case needs a correction the pinned case doesn't. Unpinned months
 * only divide what the pins leave, so a weight is worth less than a full
 * point of the chart: moving the bar one visible point takes
 * budget / remaining weight-points. Without it, dragging feels sluggish in
 * exact proportion to how much has been pinned — and perfectly normal when
 * nothing is, which is why it went unnoticed.
 */
const onDragMove = (event: PointerEvent) => {
	if (dragIndex === null) return;

	const deltaPct = (dragStartY - event.clientY) / (chartHeight / maxPct.value);

	if (dragPinned) {
		setPin(dragIndex, dragStartValue + (deltaPct / 100) * budget.value);

		return;
	}

	const weightPerChartPoint =
		remainingForFree.value > 0 ? budget.value / remainingForFree.value : 1;

	setPct(dragIndex, dragStartValue + deltaPct * weightPerChartPoint);
};

const onDragEnd = () => {
	dragIndex = null;
	window.removeEventListener("pointermove", onDragMove);
	window.removeEventListener("pointerup", onDragEnd);
};

const onBarPointerDown = (i: number, event: PointerEvent) => {
	dragIndex = i;
	dragPinned = isPinned(i);
	dragStartY = event.clientY;
	dragStartValue = dragPinned ? (pins[i] as number) ?? 0 : draft[i];
	window.addEventListener("pointermove", onDragMove);
	window.addEventListener("pointerup", onDragEnd);
};

onBeforeUnmount(() => {
	window.removeEventListener("pointermove", onDragMove);
	window.removeEventListener("pointerup", onDragEnd);
});

// --- Provenance / state ---
const canReset = computed(
	() =>
		(props.target.season_own || props.target.month_budget_own) &&
		props.target.inheritsFrom !== null
);

const resetToInherited = () => {
	const inherited = props.target.inheritsFrom;
	if (!inherited) return;

	store.setTargetOverride("season", null);
	store.setTargetOverride("month_budget", null);

	// What this target falls back to — a node's parent, for a screen the node
	// carrying it, and at the root the book's own default shape — is exactly
	// what it will now inherit, so seed from it instantly rather than waiting
	// on the debounced resolve round-trip. The inherited shape, and no pins:
	// pins are never handed down.
	seedFrom(
		inherited.monthly_season_shares,
		inherited.monthly_season_locks,
		null
	);
};

// --- Save / Cancel ---
const footColor = computed(() =>
	valid.value ? "success" : overshoot.value > 0 ? "error" : "warning"
);
/**
 * Reads like the Budget split tab's own tally, because it is now the same
 * statement one dimension over: what the twelve months add up to, against
 * what this target has to give them.
 */
const footMain = computed(
	() =>
		`Allocated ${formatNumberForScale(
			"money",
			allocated.value
		)} of ${formatNumberForScale("money", budget.value)}`
);

const footSub = computed(() => {
	if (overshoot.value > 1) {
		return `Pinned months ask ${formatNumberForScale(
			"money",
			overshoot.value
		)} more than this ${props.target.kindLabel.toLowerCase()} has — lower a pinned month.`;
	}
	if (!valid.value) {
		return `Every month is pinned, so they have to total exactly ${formatNumberForScale(
			"money",
			budget.value
		)}.`;
	}
	if (pinnedCount.value > 0) {
		return `${pinnedCount.value} pinned month${
			pinnedCount.value === 1 ? "" : "s"
		} hold their amount; the rest share what's left.`;
	}
	return "Every month takes its share of the budget. Ready to save.";
});

const save = () => {
	if (!valid.value) return;

	store.setTargetOverride("season", {
		monthly_shares: draft.slice(),
		locked_months: locked.slice(),
	});

	// Two rows, deliberately: the shape inherits down the tree, the pins
	// never do (see BasePriceOverrideType::MONTH_BUDGET). Clearing every pin
	// removes the row rather than saving twelve nulls, so a target with no
	// pins carries no month-budget row at all.
	store.setTargetOverride(
		"month_budget",
		pinnedCount.value > 0 ? { monthly_amounts: pins.slice() } : null
	);

	original.forEach((_, i) => (original[i] = draft[i]));
	originalLocked.forEach((_, i) => (originalLocked[i] = locked[i]));
	originalPins.forEach((_, i) => (originalPins[i] = pins[i]));
	dirty.value = false;
};

const cancel = () => {
	original.forEach((v, i) => {
		draft[i] = v;
		locked[i] = originalLocked[i];
		pins[i] = originalPins[i];
	});
	dirty.value = false;
};
</script>

<template>
	<div>
		<div class="d-flex flex-wrap justify-space-between align-center ga-4 mb-4">
			<plan-month-breadcrumb
				label="Monthly Budget Distribution"
				:target-name="target.name"
				:year="store.plan?.year"
				:month="calendarMonth"
				@back="calendarMonth = null"
			/>

			<div class="d-flex align-center ga-3 flex-wrap">
				<!--
					Labelled because the toggle only changes how the months are
					*read*. Which unit a month is actually stored in is per-month
					and set on its own button below the bars: an unpinned month
					holds a share whichever way this is pointing, and a pinned one
					holds euros. Unlabelled, this looked like the control that
					decided that — which is exactly what it used to be, back when
					a euro figure was converted to a percentage on the way in.
				-->
				<span
					class="text-caption text-medium-emphasis"
					title="Changes how the twelve months are displayed. To hold a month at an exact amount, use its own button below the bars."
				>
					Show as
				</span>
				<div class="mode-toggle">
					<button
						class="mode-toggle-option"
						:class="{ 'mode-toggle-option--active': mode === 'abs' }"
						@click="mode = 'abs'"
					>
						€
					</button>
					<button
						class="mode-toggle-option"
						:class="{ 'mode-toggle-option--active': mode === 'pct' }"
						@click="mode = 'pct'"
					>
						%
					</button>
				</div>

				<v-btn
					v-if="calendarMonth !== null && calendarRef?.ownsMonth"
					size="small"
					variant="tonal"
					color="error"
					prepend-icon="fa-light fa-arrow-rotate-left"
					@click="calendarRef?.resetToInherited()"
				>
					Reset to inherited
				</v-btn>

				<v-btn
					v-if="canReset && calendarMonth === null"
					size="small"
					variant="tonal"
					color="error"
					prepend-icon="fa-light fa-arrow-rotate-left"
					@click="resetToInherited"
				>
					Reset to inherited
				</v-btn>
			</div>
		</div>

		<plan-seasonality-calendar
			v-if="calendarMonth !== null"
			ref="calendarRef"
			v-model:month="calendarMonth"
			v-model:mode="mode"
			:target="target"
		/>

		<template v-else>
			<!--
			The allocation readout sits above the chart, matching the Fill & CPM
			tab's own summary card: it is what the twelve bars below are being
			measured against, so it reads better before them than after.
		-->
			<div class="season-summary-card mb-4">
				<div class="text-body-2 font-weight-bold" :class="`text-${footColor}`">
					{{ footMain }}
				</div>
				<div class="text-caption text-medium-emphasis">{{ footSub }}</div>
				<!--
				Where the budget actually goes, in the same colours the bars
				below use: pinned months first, then the locked and auto months
				sharing what is left, then any overspill past the cap.
			-->
				<div class="season-summary-bar">
					<div
						:style="{ flex: Math.min(pinnedTotal, budget) || 0.0001 }"
						class="season-summary-pinned"
						title="Pinned months — exact amounts"
					/>
					<div
						:style="{ flex: freeWeights.locked || 0.0001 }"
						class="season-summary-locked"
						title="Locked months — share set by hand"
					/>
					<div
						:style="{ flex: freeWeights.auto || 0.0001 }"
						class="season-summary-auto"
						title="Auto months — whatever is left"
					/>
					<div
						:style="{ flex: overshoot || 0.0001 }"
						class="season-summary-over"
						title="Pinned beyond the budget"
					/>
				</div>
			</div>

			<div class="d-flex ga-4 flex-wrap mb-2 text-caption text-medium-emphasis">
				<span class="d-flex align-center ga-1">
					<span
						class="legend-square"
						:style="{ background: SEASON_STATE_COLORS.auto }"
					/>
					rebalanced automatically
				</span>
				<span class="d-flex align-center ga-1">
					<span
						class="legend-square"
						:style="{ background: SEASON_STATE_COLORS.locked }"
					/>
					locked — share set by hand
				</span>
				<span class="d-flex align-center ga-1">
					<span
						class="legend-square"
						:style="{ background: SEASON_STATE_COLORS.pinned }"
					/>
					pinned — exact amount
				</span>
				<span class="d-flex align-center ga-1">
					<span class="legend-dash" />
					impression share
				</span>
			</div>

			<div style="overflow-x: auto">
				<div class="season-grid">
					<div
						v-for="(monthLabel, i) in MONTHS"
						:key="monthLabel"
						class="season-col"
					>
						<div
							class="season-bar-track"
							:style="{ height: chartHeight + 'px' }"
							@pointerdown="onBarPointerDown(i, $event)"
						>
							<div
								class="season-bar-fill"
								:style="{
									height: barHeight(i) + 'px',
									background: isPinned(i)
										? SEASON_STATE_COLORS.pinned
										: locked[i]
										? SEASON_STATE_COLORS.locked
										: SEASON_STATE_COLORS.auto,
								}"
							/>
							<div class="season-ref-line" :style="{ top: refTop(i) + 'px' }" />
							<div class="season-bar-value">{{ formatBarValue(i) }}</div>
						</div>

						<div
							class="season-input-box"
							:class="{
								'season-input-box--locked': locked[i] && !isPinned(i),
								'season-input-box--pinned': isPinned(i),
							}"
						>
							<span
								v-if="isPinned(i) || mode === 'abs'"
								class="season-input-affix"
								>€</span
							>
							<input
								:type="isCurrency(i) ? 'text' : 'number'"
								:inputmode="isCurrency(i) ? 'numeric' : undefined"
								:step="isCurrency(i) ? undefined : 0.1"
								:value="displayText(i)"
								@focus="focusedInput = i"
								@blur="focusedInput = null"
								@change="
									onInputText(i, ($event.target as HTMLInputElement).value)
								"
							/>
							<span
								v-if="!isPinned(i) && mode === 'pct'"
								class="season-input-affix"
								>%</span
							>
						</div>

						<!--
						One button, cycling AUTO → LOCKED → PINNED → AUTO. A month
						either takes whatever the others leave (AUTO), holds its
						share of the budget (LOCKED), or holds an exact euro
						amount (PINNED).
					-->
						<button
							class="season-state-btn"
							:class="{
								'season-state-btn--locked': locked[i] && !isPinned(i),
								'season-state-btn--pinned': isPinned(i),
							}"
							:title="
								isPinned(i)
									? 'Holding an exact amount — click for auto'
									: locked[i]
									? 'Holding its share — click to pin an exact amount'
									: 'Takes whatever the other months leave — click to hold its share'
							"
							@click="cycleState(i)"
						>
							{{ isPinned(i) ? "PINNED" : locked[i] ? "LOCKED" : "AUTO" }}
						</button>

						<!--
						The label opens the month by day. Disabled while the months
						have unapplied changes — see openMonth().
					-->
					<plan-month-label
						:label="monthLabel"
						:name="MONTH_NAMES[i]"
						:bold="locked[i] || isPinned(i)"
						:marked="monthsWithDays.has(i)"
						marked-hint="its days are distributed by hand"
						:error="
							monthsWithDayIssues.has(i)
								? 'The days pinned in this month ask for more than it has'
								: null
						"
						:disabled-reason="
							dirty ? 'Apply or cancel your changes to the months first' : null
						"
						@open="openMonth(i)"
					/>
						<div
							v-if="isPinned(i)"
							class="text-caption text-center season-pin-hint"
						>
							exactly this
						</div>
						<div
							v-else-if="wasChanged(i)"
							class="text-caption text-medium-emphasis text-center"
						>
							was {{ original[i].toFixed(1) }}
						</div>
					</div>
				</div>
			</div>

			<div class="d-flex ga-2 flex-wrap my-4">
				<v-btn
					size="small"
					variant="tonal"
					prepend-icon="fa-light fa-clock-rotate-left"
					@click="previousYearsOpen = true"
				>
					Derive distribution from previous years
				</v-btn>
				<plan-previous-years-dialog
					v-model:visible="previousYearsOpen"
					:target-name="target.name"
					@apply="applyPreviousYearsShares"
				/>
				<v-btn size="small" variant="tonal" @click="followImpressionShare">
					Follow impression share
				</v-btn>
				<v-btn
					v-if="pinnedCount > 0"
					size="small"
					variant="outlined"
					@click="clearAllPins"
				>
					Clear {{ pinnedCount }} pin{{ pinnedCount === 1 ? "" : "s" }}
				</v-btn>
			</div>

			<div class="d-flex justify-end ga-2">
				<v-btn variant="text" color="error" @click="cancel">Cancel</v-btn>
				<v-btn variant="tonal" color="primary" :disabled="!valid" @click="save">
					Apply seasonality
				</v-btn>
			</div>
		</template>
	</div>
</template>

<style scoped lang="scss">
.mode-toggle {
	display: flex;
	gap: 2px;
	padding: 3px;
	border-radius: 7px;
	background: rgba(var(--v-theme-on-surface), 0.06);
}

.mode-toggle-option {
	padding: 4px 10px;
	border-radius: 5px;
	font-size: 12px;
	font-weight: 600;
	cursor: pointer;
	border: 0;
	background: transparent;
	color: inherit;
}

.mode-toggle-option--active {
	background: rgb(var(--v-theme-surface));
	box-shadow: 0 1px 2px rgba(0, 0, 0, 0.12);
}

.legend-square {
	width: 9px;
	height: 9px;
	border-radius: 2px;
	display: inline-block;
}

.legend-dash {
	width: 14px;
	height: 0;
	border-top: 1.5px dashed rgba(var(--v-theme-on-surface), 0.4);
	display: inline-block;
}

.season-grid {
	display: grid;
	grid-template-columns: repeat(12, minmax(64px, 1fr));
	gap: 6px;
	min-width: 860px;
}

.season-col {
	display: flex;
	flex-direction: column;
}

.season-bar-track {
	position: relative;
	border-radius: 8px;
	background: rgba(var(--v-theme-on-surface), 0.04);
	border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
	cursor: ns-resize;
	user-select: none;
	overflow: hidden;
}

.season-bar-fill {
	position: absolute;
	left: 3px;
	right: 3px;
	bottom: 0;
	border-radius: 4px 4px 0 0;
	transition: height 0.1s ease;
}

.season-ref-line {
	position: absolute;
	left: 0;
	right: 0;
	height: 0;
	border-top: 1.5px dashed rgba(var(--v-theme-on-surface), 0.4);
}

.season-bar-value {
	position: absolute;
	left: 0;
	right: 0;
	top: 6px;
	text-align: center;
	font-size: 11px;
	font-weight: 600;
	font-variant-numeric: tabular-nums;
}

.season-input-box {
	display: flex;
	align-items: center;
	justify-content: center;
	margin-top: 8px;
	height: 28px;
	border: 1.5px solid rgba(var(--v-theme-on-surface), 0.25);
	border-radius: 6px;
	background: rgb(var(--v-theme-surface));
}

.season-input-box--locked {
	border-color: v-bind(LOCKED_COLOR);
}

.season-input-box input {
	width: 100%;
	min-width: 0;
	border: 0;
	outline: none;
	background: transparent;
	color: inherit;
	font: inherit;
	font-size: 12px;
	font-weight: 600;
	text-align: center;
	font-variant-numeric: tabular-nums;
}

.season-input-box input::-webkit-outer-spin-button,
.season-input-box input::-webkit-inner-spin-button {
	-webkit-appearance: none;
	margin: 0;
}

.season-input-affix {
	font-size: 11px;
	color: rgba(var(--v-theme-on-surface), 0.6);
	padding: 0 4px;
}

.season-input-box--pinned {
	border-color: v-bind(PINNED_COLOR);
}

// In the normal ink: the pinned colour is too light to read as text.
.season-pin-hint {
	color: rgba(var(--v-theme-on-surface), 0.7);
}

.season-state-btn {
	margin-top: 6px;
	width: 100%;
	border: 1px solid rgba(var(--v-theme-on-surface), 0.2);
	background: transparent;
	color: rgba(var(--v-theme-on-surface), 0.6);
	border-radius: 6px;
	padding: 3px 0;
	font-size: 9.5px;
	letter-spacing: 0.08em;
	cursor: pointer;
}

// Text stays in the normal ink: the state colours are too light to read.
.season-state-btn--locked {
	border-color: v-bind(LOCKED_COLOR);
	background: v-bind(LOCKED_TINT);
	color: rgb(var(--v-theme-on-surface));
}

.season-state-btn--pinned {
	border-color: v-bind(PINNED_COLOR);
	background: v-bind(PINNED_TINT);
	color: rgb(var(--v-theme-on-surface));
}

// Same surface, border and radius as PlanFillCpmTab.vue's gauge card, so the
// two tabs read as one panel rather than two separately styled ones.
.season-summary-card {
	background: rgb(var(--v-theme-surface));
	border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
	border-radius: 10px;
	padding: 14px 20px;
}

.season-summary-bar {
	display: flex;
	height: 8px;
	border-radius: 4px;
	overflow: hidden;
	background: rgba(var(--v-theme-on-surface), 0.08);
	margin-top: 8px;
}

.season-summary-pinned {
	background: v-bind(PINNED_COLOR);
}

.season-summary-locked {
	background: v-bind(LOCKED_COLOR);
}

.season-summary-auto {
	background: v-bind(AUTO_COLOR);
}

// Overspill is the pinned mass continuing past the cap, so it keeps the
// pinned colour; the divider is what marks where the budget actually ran out.
.season-summary-over {
	background: v-bind(PINNED_COLOR);
	border-left: 2px solid rgb(var(--v-theme-surface));
}
</style>
